import React, { useState, useCallback } from "react";
import MarketSelectorHeader from "components/ui/MarketSelectorHeader";
import DataRefreshIndicator from "components/ui/DataRefreshIndicator";
import ExportControlPanel from "components/ui/ExportControlPanel";
import FilterPersistenceManager, { FilterPersistenceProvider } from "components/ui/FilterPersistenceManager";
import MetricsStrip from "./components/MetricsStrip";
import FinancingControls from "./components/FinancingControls";
import CashFlowWaterfall from "./components/CashFlowWaterfall";
import OpportunityRanking from "./components/OpportunityRanking";
import ComparisonMatrix from "./components/ComparisonMatrix";
import MonteCarloPanel from "./components/MonteCarloPanel";
import SensitivityAnalysis from "./components/SensitivityAnalysis";
import ReportGenerator from "./components/ReportGenerator";

const CURRENT_PATH = "/investment-roi-dashboard";

export default function InvestmentROIDashboard() {
  const [settings, setSettings] = useState({
    loanType: "conventional",
    holdPeriod: 5,
    targetROI: 7,
    scenario: "base",
  });

  const handleSettingChange = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleRefresh = useCallback(async () => {
    await new Promise((r) => setTimeout(r, 800));
  }, []);

  const handleExport = useCallback(async (option) => {
    await new Promise((r) => setTimeout(r, 1000));
  }, []);

  return (
    <FilterPersistenceProvider>
      <div className="min-h-screen bg-background">
        {/* Header + Nav */}
        <MarketSelectorHeader />

        {/* Page offset for fixed header (60px) + tab nav (49px) */}
        <div className="pt-[109px] lg:pt-[109px] pb-20 lg:pb-8">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8">

            {/* Page Header */}
            <div className="flex items-start justify-between gap-4 py-5 flex-wrap">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                  Investment ROI Dashboard
                </h1>
                <p className="text-sm md:text-base font-caption text-muted-foreground mt-1">
                  Comprehensive financial analysis · Cash flow projections · Portfolio ranking
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <DataRefreshIndicator onRefresh={handleRefresh} />
                <FilterPersistenceManager currentPath={CURRENT_PATH} />
                <ExportControlPanel currentPath={CURRENT_PATH} onExport={handleExport} />
              </div>
            </div>

            {/* Financing Controls */}
            <div className="mb-6">
              <FinancingControls settings={settings} onChange={handleSettingChange} />
            </div>

            {/* Metrics Strip */}
            <div className="mb-6">
              <MetricsStrip />
            </div>

            {/* Main Analysis Area */}
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 mb-6">
              {/* Cash Flow Waterfall - 6 cols */}
              <div className="lg:col-span-6">
                <CashFlowWaterfall scenario={settings?.scenario} />
              </div>
              {/* Opportunity Ranking - 4 cols */}
              <div className="lg:col-span-4">
                <OpportunityRanking targetROI={settings?.targetROI} />
              </div>
            </div>

            {/* Monte Carlo + Sensitivity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <MonteCarloPanel />
              <SensitivityAnalysis />
            </div>

            {/* Full-width Comparison Matrix */}
            <div className="mb-6">
              <ComparisonMatrix />
            </div>

            {/* Report Generator */}
            <div className="mb-6">
              <ReportGenerator />
            </div>

          </div>
        </div>
      </div>
    </FilterPersistenceProvider>
  );
}